/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { Injectable, inject } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Supabase } from "@app/@classes/supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { Subject, takeUntil } from "rxjs";
import { AuthStateService } from "../shared/auth-state.service";
import { FeaturesService } from "../shared/features.service";

const PRESENCE_ROOM = "users_presence";

interface PresenceStatus {
	user_id?: string;
	status?: "online" | "offline" | "inactive";
	email?: string;
	online_at?: string;
	route?: string;
	device?: "desktop" | "mobile" | "tablet";
}

@Injectable({
	providedIn: "root",
})
export class RealtimeOnlinePresenceService extends Supabase {
	private readonly authStateService: AuthStateService = inject(AuthStateService);
	private readonly featureService: FeaturesService = inject(FeaturesService);

	private readonly presenceDestroy$ = new Subject<void>();

	private presenceChannel?: RealtimeChannel;

	private _userStatus: PresenceStatus = {};
	private _presenceTrackStatus: PresenceStatus[] = [];

	constructor() {
		super();

		toObservable(this.authStateService.currentTenantId)
			.pipe(takeUntil(this.presenceDestroy$))
			.subscribe((tenantId) => {
				if (!tenantId) {
					this.destroyPresence();
				} else {
					if (this.presenceChannel) return;

					this.presenceChannel = this.supabaseClient.channel(
						`${PRESENCE_ROOM}_${tenantId}`,
					);
					this.initPresence();
				}
			});

		this.supabaseClient.auth.onAuthStateChange((event, session) => {
			if (session?.user) {
				this._userStatus = {
					user_id: session.user.id,
					status: "online",
					email: session.user.email,
					online_at: new Date().toISOString(),
					device: this.currentUserDevice,
				};

				if (this.presenceChannel) {
					this.presenceChannel.track(this._userStatus);
				}
			} else {
				this.destroyPresence();
			}
		});
	}

	public get currentUserDevice(): PresenceStatus["device"] {
		const userAgent = navigator.userAgent;
		if (/mobile/i.test(userAgent)) {
			return "mobile";
		}

		return "desktop";
	}

	public currentUserHasDesktopOpen(): boolean {
		return this._presenceTrackStatus.some(
			(status) =>
				status.user_id === this._userStatus.user_id &&
				status.device === "desktop",
		);
	}

	public currentUserHasMobileOpen(): boolean {
		return this._presenceTrackStatus.some(
			(status) =>
				status.user_id === this._userStatus.user_id &&
				status.device === "mobile",
		);
	}

	public get hasRealtime(): boolean {
		return this.featureService.canSelect(["realtime"]);
	}

	public get onlineUsersIds() {
		return this._presenceTrackStatus.map((status) => status.user_id);
	}

	private initPresence() {
		if (!this.presenceChannel) return;

		this.presenceChannel
			.on("presence", { event: "sync" }, () => {
				if (!this.presenceChannel) return;

				const newState = this.presenceChannel.presenceState();
				this._presenceTrackStatus = Object.keys(newState).flatMap(
					(key) => newState[key] as PresenceStatus[],
				);
			})
			.on("presence", { event: "join" }, ({ key, newPresences }) => { })
			.on("presence", { event: "leave" }, ({ key, leftPresences }) => { })
			.subscribe(async (status) => {
				if (status !== "SUBSCRIBED") return;
				if (!this.presenceChannel) return;

				if (this._userStatus.user_id) {
					this.presenceChannel.track(this._userStatus);
				}
			});
	}

	private destroyPresence() {
		this._userStatus = {};
		this.presenceChannel?.untrack(this._userStatus);
		this.presenceChannel?.unsubscribe();
		this.presenceChannel = undefined;
	}

	ngOnDestroy(): void {
		this.presenceDestroy$.next();
		this.presenceDestroy$.complete();
	}
}
